import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  GiftOutlined,
  MenuOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Avatar,
  Button,
  Empty,
  Form,
  Input,
  Modal,
  message,
  Popconfirm,
  Space,
  Table,
  Tag,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { raffleDelete, raffleErrorKey, rafflePatch, rafflePost } from '../config/raffleApi';
import ImageUpload from './ImageUpload';
import { LogoThumb } from './LogoThumb';

const { TextArea } = Input;

export interface RafflePrize {
  id: string;
  name: string;
  description?: string;
  sponsorName?: string;
  sponsorLogo?: string;
  image?: string;
  order: number;
  status: 'PENDING' | 'DRAWING' | 'AWARDED';
}

interface RafflePrizeListProps {
  drawId: string;
  prizes: RafflePrize[];
  /** Re-read the round after any write — the parent owns the data. */
  onChanged: () => void;
  /**
   * A finished round is read-only. The backend rejects the writes anyway
   * (`RAFFLE_PRIZE_ALREADY_AWARDED`); this only keeps the buttons from being
   * offered in the first place.
   */
  readOnly?: boolean;
}

interface PrizeFormValues {
  name: string;
  description?: string;
  sponsorName?: string;
  sponsorLogo?: string;
  image?: string;
}

const prizeStatusConfig = {
  PENDING: { color: 'default', labelKey: 'raffle.prizeStatus.pending' },
  DRAWING: { color: 'processing', labelKey: 'raffle.prizeStatus.drawing' },
  AWARDED: { color: 'success', labelKey: 'raffle.prizeStatus.awarded' },
} as const;

/**
 * Same drag-handle row as `pages/FAQs.tsx` — the panel should not invent a
 * second reordering gesture for the one it already teaches elsewhere. Typed
 * rather than `any`: antd hands the row component the row key through this
 * data attribute, and that is the id `useSortable` needs.
 */
interface SortableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const SortableRow: React.FC<SortableRowProps> = ({ children, ...props }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props['data-row-key'],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'move',
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style}>
      {React.Children.map(children, (child) => {
        if ((child as React.ReactElement).key === 'sort') {
          return React.cloneElement(child as React.ReactElement<{ children?: React.ReactNode }>, {
            children: (
              <MenuOutlined
                style={{ cursor: 'move', color: '#999' }}
                {...attributes}
                {...listeners}
              />
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

const RafflePrizeList: React.FC<RafflePrizeListProps> = ({
  drawId,
  prizes,
  onChanged,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPrize, setEditingPrize] = useState<RafflePrize | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<PrizeFormValues>();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleCreate = () => {
    setEditingPrize(null);
    setModalVisible(true);
  };

  const handleEdit = (record: RafflePrize) => {
    setEditingPrize(record);
    setModalVisible(true);
  };

  const handleSubmit = async (values: PrizeFormValues) => {
    setSaving(true);
    try {
      if (editingPrize) {
        await rafflePatch(`/raffle/draws/${drawId}/prizes/${editingPrize.id}`, values);
        message.success(t('raffle.prizes.updated'));
      } else {
        // `order` is left to the backend: it appends, and sending a guessed
        // index here would fight the reorder endpoint for the same column.
        await rafflePost(`/raffle/draws/${drawId}/prizes`, values);
        message.success(t('raffle.prizes.created'));
      }
      setModalVisible(false);
      onChanged();
    } catch (error) {
      message.error(t(raffleErrorKey(error)));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (prizeId: string) => {
    try {
      await raffleDelete(`/raffle/draws/${drawId}/prizes/${prizeId}`);
      message.success(t('raffle.prizes.deleted'));
      onChanged();
    } catch (error) {
      message.error(t(raffleErrorKey(error)));
    }
  };

  /**
   * Persist an order. The endpoint takes the WHOLE id list, so the caller hands
   * over the already-reordered array rather than a pair of indices.
   */
  const persistOrder = async (ordered: RafflePrize[]) => {
    try {
      await rafflePatch(`/raffle/draws/${drawId}/prizes/reorder`, {
        prizeIds: ordered.map((prize) => prize.id),
      });
      onChanged();
    } catch (error) {
      message.error(t(raffleErrorKey(error)));
      // Re-read on failure: the rows on screen are in the order the drag left
      // them, which is now a lie about what the server holds.
      onChanged();
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = prizes.findIndex((prize) => prize.id === active.id);
    const newIndex = prizes.findIndex((prize) => prize.id === over.id);
    persistOrder(arrayMove(prizes, oldIndex, newIndex));
  };

  /**
   * Keyboard- and touch-friendly alternative to dragging. The panel is used on
   * a laptop on a lectern, where a precise drag is genuinely awkward.
   */
  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= prizes.length) return;
    persistOrder(arrayMove(prizes, index, target));
  };

  const columns: ColumnsType<RafflePrize> = [
    {
      key: 'sort',
      width: 30,
      render: () => null,
    },
    {
      title: '#',
      key: 'position',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: t('raffle.prizes.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record) => (
        <Space>
          {record.image ? (
            <Avatar shape="square" src={record.image} />
          ) : (
            <Avatar shape="square" icon={<GiftOutlined />} />
          )}
          <span>{name}</span>
        </Space>
      ),
    },
    {
      title: t('raffle.prizes.sponsor'),
      dataIndex: 'sponsorName',
      key: 'sponsorName',
      render: (sponsorName: string, record) => (
        <Space>
          {record.sponsorLogo && (
            <LogoThumb src={record.sponsorLogo} alt={sponsorName || ''} width={48} height={24} />
          )}
          <span>{sponsorName || '-'}</span>
        </Space>
      ),
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: RafflePrize['status']) => {
        const config = prizeStatusConfig[status];
        return <Tag color={config.color}>{t(config.labelKey)}</Tag>;
      },
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 190,
      render: (_, record, index) => (
        <Space size="small">
          <Button
            type="text"
            icon={<ArrowUpOutlined />}
            disabled={readOnly || index === 0}
            onClick={() => handleMove(index, -1)}
            aria-label={t('raffle.prizes.moveUp')}
          />
          <Button
            type="text"
            icon={<ArrowDownOutlined />}
            disabled={readOnly || index === prizes.length - 1}
            onClick={() => handleMove(index, 1)}
            aria-label={t('raffle.prizes.moveDown')}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            disabled={readOnly}
            onClick={() => handleEdit(record)}
            aria-label={t('common.edit')}
          />
          <Popconfirm
            title={t('raffle.prizes.confirmDelete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
            disabled={readOnly || record.status === 'AWARDED'}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={readOnly || record.status === 'AWARDED'}
              aria-label={t('common.delete')}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} disabled={readOnly}>
          {t('raffle.prizes.add')}
        </Button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={prizes.map((prize) => prize.id)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            components={{ body: { row: SortableRow } }}
            columns={columns}
            dataSource={prizes}
            rowKey="id"
            pagination={false}
            size="small"
            locale={{
              emptyText: <Empty description={t('raffle.prizes.empty')} />,
            }}
          />
        </SortableContext>
      </DndContext>

      <Modal
        title={editingPrize ? t('raffle.prizes.editTitle') : t('raffle.prizes.newTitle')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        destroyOnHidden
        width={640}
      >
        {/* `destroyOnHidden` unmounts the form on close, so the fields are
            seeded through `initialValues` on the next mount rather than with an
            imperative `setFieldsValue` — calling that while the form is
            unmounted is what antd warns about as a disconnected instance. */}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            name: editingPrize?.name,
            description: editingPrize?.description,
            sponsorName: editingPrize?.sponsorName,
            sponsorLogo: editingPrize?.sponsorLogo,
            image: editingPrize?.image,
          }}
        >
          <Form.Item
            name="name"
            label={t('raffle.prizes.name')}
            rules={[{ required: true, message: t('common.requiredField') }]}
          >
            <Input placeholder={t('raffle.prizes.namePlaceholder')} />
          </Form.Item>

          <Form.Item name="description" label={t('common.description')}>
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item name="sponsorName" label={t('raffle.prizes.sponsor')}>
            <Input />
          </Form.Item>

          {/* Both image fields go through the existing upload module — the
              sponsor logo keeps the URL input for the same reason the sponsor
              page does: today's logos point at the sponsor's own site. */}
          <Form.Item name="sponsorLogo" label={t('raffle.prizes.sponsorLogo')}>
            <ImageUpload uploadUrl="/api/upload/logo" fieldName="logo" maxSize={2} allowUrlInput />
          </Form.Item>

          <Form.Item name="image" label={t('raffle.prizes.image')}>
            <ImageUpload uploadUrl="/api/upload/image" fieldName="image" maxSize={5} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default RafflePrizeList;
